import clsx from 'clsx';
import React, { isValidElement } from 'react';
import { Link } from '../../Buttons/Link/Link.mjs';
import { Checkbox } from '../../Controls/Checkbox/Checkbox.mjs';
import { Tag } from '../../DataDisplay/Tag/Tag.mjs';
import { polymorphicFactory } from '../../factory/factory.utils.mjs';
import { Typography } from '../../Foundations/Typography/Typography.mjs';
import { Flex } from '../../Layout/Flex/Flex.mjs';
import { Box } from '../../Other/Box/Box.mjs';
import { omitText } from '../../utils/helpers.mjs';
import classes from './ProductCard.module.css.mjs';
import { productCardDetailsSpacingClasses, productCardDetailsClasses, productCardPriceClasses, productCardDetailsRightSectionClasses, productCardSlashedPriceClasses } from './ProductCard.utils.mjs';
import { ColorSwatches } from './subcomponents/ColorSwatches/ColorSwatches.mjs';
import { ProductCardImage } from './subcomponents/ProductCardImage/ProductCardImage.mjs';

const ProductCard = polymorphicFactory(
  (props, ref) => {
    const {
      component = "div",
      image,
      isSaved,
      onSave,
      onShare,
      saveCTA,
      shareCTA,
      sharePopover,
      quickViewCTA,
      title,
      description,
      price,
      slashedPrice,
      type,
      href,
      productDetails,
      className,
      ...rest
    } = props;
    const isProductCard = type === "Product Card";
    const isAccountCard = type === "Account Card";
    const isProductListSlider = type === "Product List Slider";
    const isShopTheRoom = type === "Shop The Room";
    const hasLink = !!(href && isProductCard);
    const showProductInformation = productDetails || title || description || price || slashedPrice;
    if (onSave) console.warn("Property `onSave` will be deprecated soon. Please use `saveCTA` instead.");
    if (onShare) console.warn("Property `onShare` will be deprecated soon. Please use `shareCTA` instead.");
    return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
      Box,
      {
        ref,
        className: clsx("kohler-product-card-root", className, classes.productCardContainer),
        component: hasLink ? "div" : component,
        href: !hasLink ? href : void 0,
        ...rest
      },
      /* @__PURE__ */ React.createElement(
        ProductCardImage,
        {
          hasLink,
          href,
          image,
          onSave,
          isSaved,
          onShare,
          saveCTA,
          shareCTA,
          sharePopover,
          quickViewCTA
        }
      ),
      showProductInformation && /* @__PURE__ */ React.createElement(Flex, { className: productCardDetailsSpacingClasses[type] }, /* @__PURE__ */ React.createElement(Flex, { className: clsx(classes.productCardDetailsLeft, productCardDetailsClasses[type]) }, isProductCard && productDetails && (productDetails.colorSwatches || productDetails.checkbox) && /* @__PURE__ */ React.createElement(
        Flex,
        {
          className: clsx(
            classes.productCardDetailsSwatchesContainer,
            !productDetails.colorSwatches && productDetails.checkbox && classes.showMobileOnly
          )
        },
        productDetails.colorSwatches && /* @__PURE__ */ React.createElement(ColorSwatches, { ...productDetails.colorSwatches }),
        productDetails.checkbox && /* @__PURE__ */ React.createElement(Checkbox, { ...productDetails.checkbox, className: classes.productCardCheckbox })
      ), showProductInformation && /* @__PURE__ */ React.createElement(Flex, { gap: 5, direction: "column" }, isProductCard && productDetails && (productDetails.tags || productDetails.additionalLink) && /* @__PURE__ */ React.createElement(
        Flex,
        {
          className: clsx(
            classes.productDetailsTagsContainer,
            !productDetails.tags && productDetails.additionalLink && classes.showMobileOnly
          )
        },
        productDetails.tags && /* @__PURE__ */ React.createElement(Flex, { gap: 10 }, productDetails.tags.map((tag, index) => /* @__PURE__ */ React.createElement(Tag, { key: index, ...tag }))),
        productDetails.additionalLink && // eslint-disable-next-line @typescript-eslint/no-explicit-any
        /* @__PURE__ */ React.createElement(
          Link,
          {
            component: productDetails.additionalLink.onClick ? "button" : "a",
            className: classes.productCardAdditionalLink,
            ...omitText(productDetails.additionalLink),
            size: "small"
          },
          productDetails.additionalLink.text
        )
      ), (isShopTheRoom || isAccountCard) && productDetails && productDetails.brand && /* @__PURE__ */ React.createElement(
        Typography,
        {
          ...omitText(productDetails.brand),
          className: clsx(classes.productCardTertiaryText, productDetails.brand.className),
          size: "label-medium-s",
          component: isValidElement(productDetails.brand.text) ? "div" : productDetails.brand.component || "p"
        },
        productDetails.brand.text
      ), title && /* @__PURE__ */ React.createElement(
        Typography,
        {
          ...omitText(title),
          className: clsx(
            isProductListSlider ? classes.productCardPrimaryText : classes.productCardSecondaryText,
            title.className
          ),
          size: "label-regular-l",
          component: title.component || "span"
        },
        title.text
      ), description && /* @__PURE__ */ React.createElement(
        Typography,
        {
          ...omitText(description),
          className: clsx(classes.productCardTertiaryText, description.className),
          size: "paragraph-medium",
          component: isValidElement(description.text) ? "div" : description.component || "p"
        },
        description.text
      )), (isProductListSlider || isAccountCard) && productDetails && productDetails.availableColors && /* @__PURE__ */ React.createElement(
        Typography,
        {
          ...omitText(productDetails.availableColors),
          className: clsx(
            isProductListSlider ? classes.productCardSecondaryText : classes.productCardTertiaryText,
            productDetails.availableColors.className
          ),
          size: "paragraph-medium",
          component: isValidElement(productDetails.availableColors.text) ? "div" : productDetails.availableColors.component || "span"
        },
        productDetails.availableColors.text
      ), isProductCard && productDetails && productDetails.SKU && /* @__PURE__ */ React.createElement(
        Typography,
        {
          ...omitText(productDetails.SKU),
          className: clsx(classes.productCardTertiaryText, productDetails.SKU.className),
          size: "label-regular-s",
          component: isValidElement(productDetails.SKU.text) ? "div" : productDetails.SKU.component || "span"
        },
        productDetails.SKU.text
      ), isProductCard && productDetails && productDetails.bundleItems && /* @__PURE__ */ React.createElement(
        Typography,
        {
          ...omitText(productDetails.bundleItems),
          className: clsx(classes.productCardTertiaryText, productDetails.bundleItems.className),
          size: "label-regular-s",
          component: isValidElement(productDetails.bundleItems.text) ? "div" : productDetails.bundleItems.component || "span"
        },
        productDetails.bundleItems.text
      ), (price || slashedPrice || isProductCard && productDetails && productDetails.taxesLabel) && /* @__PURE__ */ React.createElement(
        Flex,
        {
          className: clsx(
            (isProductListSlider || isAccountCard) && classes.productCardPricesLeft,
            isProductCard && classes.productCardPricesLeftColumn
          )
        },
        (price || slashedPrice) && /* @__PURE__ */ React.createElement(
          Flex,
          {
            direction: isProductCard || isShopTheRoom ? "row" : "column",
            wrap: "wrap",
            className: clsx((isProductCard || isShopTheRoom) && classes.productCardPriceContainerGap)
          },
          price && /* @__PURE__ */ React.createElement(
            Typography,
            {
              ...omitText(price),
              className: clsx(productCardPriceClasses[type], price.className),
              size: "label-medium-m",
              component: isValidElement(price.text) ? "div" : price.component || "span"
            },
            price.text
          ),
          slashedPrice && /* @__PURE__ */ React.createElement(
            Typography,
            {
              ...omitText(slashedPrice),
              className: (slashedPrice.className),
              size: "label-regular-m",
              component: isValidElement(slashedPrice.text) ? "div" : slashedPrice.component || "span",
              textDecoration: "line-through"
            },
            slashedPrice.text
          )
        ),
        isProductCard && productDetails && productDetails.taxesLabel && /* @__PURE__ */ React.createElement(
          Typography,
          {
            ...omitText(productDetails.taxesLabel),
            className: clsx(classes.productCardSecondaryText, productDetails.taxesLabel.className),
            size: "label-medium-s",
            component: isValidElement(productDetails.taxesLabel.text) ? "div" : productDetails.taxesLabel.component || "span",
            textTransform: "uppercase"
          },
          productDetails.taxesLabel.text
        )
      ), isProductCard && productDetails && productDetails.outOfStockLabel && /* @__PURE__ */ React.createElement(
        Typography,
        {
          ...omitText(productDetails.outOfStockLabel),
          className: clsx(classes.productCardTertiaryText, productDetails.outOfStockLabel.className),
          size: "paragraph-small",
          component: isValidElement(productDetails.outOfStockLabel.text) ? "div" : productDetails.outOfStockLabel.component || "span"
        },
        productDetails.outOfStockLabel.text
      )), productDetails && (isProductCard && (productDetails.checkbox || productDetails.additionalLink) || (isProductListSlider || isAccountCard) && (price || slashedPrice)) && /* @__PURE__ */ React.createElement("div", { className: productCardDetailsRightSectionClasses[type] }, isProductCard && productDetails.checkbox && /* @__PURE__ */ React.createElement(Checkbox, { ...productDetails.checkbox, className: classes.productCardCheckboxRight }), isProductCard && productDetails.additionalLink && // eslint-disable-next-line @typescript-eslint/no-explicit-any
      /* @__PURE__ */ React.createElement(
        Link,
        {
          component: productDetails.additionalLink.onClick ? "button" : "a",
          ...omitText(productDetails.additionalLink),
          size: "small"
        },
        productDetails.additionalLink.text
      ), (isProductListSlider || isAccountCard) && price && /* @__PURE__ */ React.createElement(
        Typography,
        {
          ...omitText(price),
          className: clsx(productCardPriceClasses[type], price.className),
          size: "label-medium-m",
          component: price.component || "span"
        },
        price.text
      ), (isProductListSlider || isAccountCard) && slashedPrice && /* @__PURE__ */ React.createElement(
        Typography,
        {
          ...omitText(slashedPrice),
          className: clsx(productCardSlashedPriceClasses[type], slashedPrice.className),
          size: "label-regular-m",
          component: slashedPrice.component || "span",
          textDecoration: "line-through"
        },
        slashedPrice.text
      )))
    ));
  }
);

export { ProductCard };
